import { useContext, useMemo, useState, useEffect, useCallback } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  Radar,
} from "recharts";
import "../../../styles/panel/user/Dashboard.css";
import { AuthContext } from "../../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { getDashboardData } from "../../../api/dashboard";
import moment from "jalali-moment";
import { convertToPersianDigits } from "../../../utils/stringUtil";

/* ---------- لینک موقت ویدیو آموزشی آپارات ---------- */
const VIRTUAL_TRYON_TUTORIAL_URL = "https://aparat.com/v/aic6n43";

/* ---------- آیکن‌های کوچک (هم‌ساختار با Sidebar) ---------- */
const Icon = ({ children }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {children}
  </svg>
);

/* ---------- تولتیپ مشترک نمودارها (recharts) ---------- */
const ChartTooltip = ({ active, payload, label, unit }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="chart-tooltip">
      <span className="chart-tooltip-label">{label}</span>
      <span className="chart-tooltip-value">
        {payload[0].value.toLocaleString("fa-IR")}
        {unit ? ` ${unit}` : ""}
      </span>
    </div>
  );
};

const axisTick = { fontSize: 10.5, fill: "rgba(43, 18, 76, 0.5)" };

/* ---------- تیک عمودی برای روزهای هفته ---------- */
const DayTick = ({ x, y, payload }) => (
  <g className="dash-day-tick" transform={`translate(${x},${y})`}>
    <text
      x={-10}
      y={0}
      dy={12}
      textAnchor="start"
      transform="rotate(-90)"
      fill="rgba(43, 18, 76, 0.5)"
      fontSize={10.5}
      fontFamily="inherit"
    >
      {payload?.value}
    </text>
  </g>
);

/* ---------- نمودار میله‌ای (برای نمای هفتگی) ---------- */
const SalesBarChart = ({ data, reverse = true }) => (
  <div className="chart-container">
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={reverse ? [...data].reverse() : data}
        margin={{ top: 24, right: 4, left: 4, bottom: 0 }}
      >
        <defs>
          <linearGradient id="barFillGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--rose-500)" />
            <stop offset="100%" stopColor="var(--mauve-700)" />
          </linearGradient>
        </defs>
        <CartesianGrid
          stroke="rgba(43, 18, 76, 0.09)"
          strokeDasharray="4 4"
          vertical={true}
          horizontal={true}
        />
        <XAxis
          dataKey="day"
          tickLine={false}
          axisLine={{ stroke: "rgba(43, 18, 76, 0.15)" }}
          tick={<DayTick />}
          interval={0}
          height={70}
        />
        <YAxis
          axisLine={{ stroke: "rgba(43, 18, 76, 0.15)" }}
          tickLine={false}
          tick={axisTick}
          width={34}
          domain={[0, (max) => max * 1.15]}
        />
        <Tooltip
          content={<ChartTooltip />}
          cursor={{ fill: "rgba(82, 43, 91, 0.07)" }}
        />
        <Bar
          dataKey="value"
          fill="url(#barFillGradient)"
          radius={[8, 8, 8, 8]}
          maxBarSize={32}
        />
      </BarChart>
    </ResponsiveContainer>
  </div>
);

/* ---------- نمودار خطی/ناحیه‌ای (برای نمای ماهانه و بازدید) ---------- */
const gradientCounter = { current: 0 };
const SalesAreaChart = ({ data, unit }) => {
  const gradientId = useMemo(
    () => `areaFillGradient-${gradientCounter.current++}`,
    [],
  );
  return (
    <div className="chart-container">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={[...data]}
          margin={{ top: 24, right: 8, left: 8, bottom: 0 }}
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop
                offset="0%"
                stopColor="var(--mauve-700)"
                stopOpacity={0.3}
              />
              <stop
                offset="100%"
                stopColor="var(--mauve-700)"
                stopOpacity={0}
              />
            </linearGradient>
          </defs>
          <CartesianGrid
            stroke="rgba(43, 18, 76, 0.09)"
            strokeDasharray="4 4"
            vertical={true}
            horizontal={true}
          />
          <XAxis
            dataKey="day"
            tickLine={false}
            axisLine={{ stroke: "rgba(43, 18, 76, 0.15)" }}
            tick={axisTick}
            interval="preserveStartEnd"
            dy={6}
          />
          <YAxis
            axisLine={{ stroke: "rgba(43, 18, 76, 0.15)" }}
            tickLine={false}
            tick={axisTick}
            width={34}
            domain={[0, (max) => max * 1.15]}
          />
          <Tooltip content={<ChartTooltip unit={unit} />} />
          <Area
            type="monotone"
            dataKey="value"
            stroke="var(--mauve-700)"
            strokeWidth={2.5}
            fill={`url(#${gradientId})`}
            dot={{
              r: 3.5,
              fill: "var(--white)",
              stroke: "var(--mauve-700)",
              strokeWidth: 2,
            }}
            activeDot={{
              r: 5.5,
              fill: "var(--rose-500)",
              stroke: "var(--rose-500)",
            }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

/* ---------- نمودار راداری بازدید (ماهانه/سالانه) ---------- */
const VisitsRadarChart = ({ data, unit }) => (
  <div className="chart-container chart-container-radar">
    <ResponsiveContainer width="100%" height="100%">
      <RadarChart data={data} outerRadius="72%">
        <defs>
          <linearGradient id="visitsRadarFill" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="var(--rose-500)" stopOpacity={0.55} />
            <stop
              offset="100%"
              stopColor="var(--mauve-700)"
              stopOpacity={0.35}
            />
          </linearGradient>
        </defs>
        <PolarGrid stroke="rgba(43, 18, 76, 0.12)" />
        <PolarAngleAxis dataKey="day" tick={axisTick} tickLine={false} />
        <Tooltip content={<ChartTooltip unit={unit} />} />
        <Radar
          dataKey="value"
          stroke="var(--mauve-700)"
          strokeWidth={2}
          fill="url(#visitsRadarFill)"
          dot={{
            r: 2.5,
            fill: "var(--mauve-700)",
            stroke: "var(--white)",
            strokeWidth: 1,
          }}
        />
      </RadarChart>
    </ResponsiveContainer>
  </div>
);

/* ---------- نمودار دایره‌ای وضعیت سفارش‌ها ---------- */
const StatusDonutChart = ({ data }) => (
  <ResponsiveContainer width="100%" height="100%" className="donut-svg">
    <PieChart>
      <Pie
        data={data}
        dataKey="percentage"
        nameKey="label"
        cx="50%"
        cy="50%"
        innerRadius="72%"
        outerRadius="100%"
        startAngle={90}
        endAngle={-270}
        stroke="none"
      >
        {data.map((s) => (
          <Cell key={s.id} fill={s.color} />
        ))}
      </Pie>
      <Tooltip content={<ChartTooltip unit="٪" />} />
    </PieChart>
  </ResponsiveContainer>
);

const ICONS = {
  wallet: (
    <>
      <rect x="3" y="6" width="18" height="13" rx="2.4" />
      <path d="M16 13.2h2.2" />
      <path d="M3 9.5h18" />
    </>
  ),
  sales: (
    <>
      <path d="M4 19V10M10 19V5M16 19v-7M22 19H2" />
    </>
  ),
  orders: (
    <>
      <path d="M4 7h16l-1.4 11.2a2 2 0 01-2 1.8H7.4a2 2 0 01-2-1.8L4 7z" />
      <path d="M8 7V5.5A3.5 3.5 0 0111.5 2h1A3.5 3.5 0 0116 5.5V7" />
    </>
  ),
  commission: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M9 12.5l2 2 4-4.5" />
    </>
  ),
  arrowUp: (
    <>
      <path d="M6 15l6-6 6 6" />
    </>
  ),
  arrowDown: (
    <>
      <path d="M6 9l6 6 6-6" />
    </>
  ),
  ticket: (
    <>
      <path d="M4 8.5a2 2 0 012-2h12a2 2 0 012 2v1.7a1.6 1.6 0 000 3.2V15a2 2 0 01-2 2H6a2 2 0 01-2-2v-1.6a1.6 1.6 0 000-3.2V8.5z" />
    </>
  ),
  customer: (
    <>
      <circle cx="9" cy="8" r="3" />
      <path d="M3.5 19c.5-3.4 3-5.3 5.5-5.3S14 15.6 14.5 19" />
      <circle cx="17" cy="9.5" r="2.3" />
      <path d="M15.3 13.6c1.9.2 3.4 1.7 3.8 4.4" />
    </>
  ),
  site: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3c2.4 2.6 3.6 5.7 3.6 9s-1.2 6.4-3.6 9c-2.4-2.6-3.6-5.7-3.6-9s1.2-6.4 3.6-9z" />
    </>
  ),
  visits: (
    <>
      <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  externalLink: (
    <>
      <path d="M14 4h6v6" />
      <path d="M20 4L10 14" />
      <path d="M18 13v5a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2h5" />
    </>
  ),
  product: (
    <>
      <path d="M20 7.5L12 3 4 7.5v9L12 21l8-4.5v-9z" />
      <path d="M4 7.5L12 12l8-4.5M12 12v9" />
    </>
  ),
  bell: (
    <>
      <path d="M6 10a6 6 0 0112 0c0 4 1.4 5.6 2 6.2H4c.6-.6 2-2.2 2-6.2z" />
      <path d="M10 19.5a2 2 0 004 0" />
    </>
  ),
  star: (
    <>
      <path d="M12 3.5l2.6 5.4 5.9.8-4.3 4.2 1 5.9-5.2-2.8-5.2 2.8 1-5.9-4.3-4.2 5.9-.8L12 3.5z" />
    </>
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3.2" />
      <path d="M19.4 13.5a1.7 1.7 0 00.3 1.9l.1.1a2.1 2.1 0 11-3 3l-.1-.1a1.7 1.7 0 00-1.9-.3 1.7 1.7 0 00-1 1.6v.2a2.1 2.1 0 01-4.2 0v-.1a1.7 1.7 0 00-1.1-1.6 1.7 1.7 0 00-1.9.3l-.1.1a2.1 2.1 0 11-3-3l.1-.1a1.7 1.7 0 00.3-1.9 1.7 1.7 0 00-1.6-1h-.2a2.1 2.1 0 010-4.2h.1a1.7 1.7 0 001.6-1.1 1.7 1.7 0 00-.3-1.9l-.1-.1a2.1 2.1 0 113-3l.1.1a1.7 1.7 0 001.9.3h.1a1.7 1.7 0 001-1.6v-.2a2.1 2.1 0 014.2 0v.1a1.7 1.7 0 001 1.6h.1a1.7 1.7 0 001.9-.3l.1-.1a2.1 2.1 0 113 3l-.1.1a1.7 1.7 0 00-.3 1.9v.1a1.7 1.7 0 001.6 1h.2a2.1 2.1 0 010 4.2h-.1a1.7 1.7 0 00-1.6 1z" />
    </>
  ),
  video: (
    <>
      <polygon points="23 7 16 12 23 17 23 7" />
      <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
    </>
  ),
  sparkle: (
    <>
      <path d="M12 2l2.4 6.9L21.3 12l-6.9 3.1L12 22l-2.4-6.9L2.7 12l6.9-3.1L12 2z" />
    </>
  ),
  token: (
    <>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="4.5" />
    </>
  ),
};

const PERSIAN_MONTHS = [
  "فروردین",
  "اردیبهشت",
  "خرداد",
  "تیر",
  "مرداد",
  "شهریور",
  "مهر",
  "آبان",
  "آذر",
  "دی",
  "بهمن",
  "اسفند",
];

function persianMonthName(month) {
  const n = Number(month);
  if (Number.isFinite(n) && n >= 1 && n <= 12) return PERSIAN_MONTHS[n - 1];
  return month;
}

/* ---------- داده‌ی فیک موقت برای چارت‌های بازدید ---------- */
const FAKE_MONTHLY_VISITS = Array.from({ length: 30 }, (_, i) => ({
  day: (i + 1).toLocaleString("fa-IR"),
  value: Math.floor(Math.random() * 130) + 20,
}));
const FAKE_YEARLY_VISITS = PERSIAN_MONTHS.map((m) => ({
  day: m,
  value: Math.floor(Math.random() * 1800) + 200,
}));

/* ---------- رنگ هر وضعیت سفارش ---------- */
const ORDER_STATUS_COLORS = {
  pending: "var(--cream-100)",
  approved: "var(--blush-300)",
  preparing: "var(--rose-500)",
  shipped: "var(--mauve-700)",
  delivered: "var(--plum-900)",
  cancelled: "var(--gray-400, #9ca3af)",
};

/* ---------- تنظیمات نمایشی کارت‌های آماری ---------- */
const STAT_META = {
  wallet: { label: "موجودی کیف پول", unit: "تومان", icon: "wallet" },
  sales: { label: "فروش این ماه", unit: "تومان", icon: "sales" },
  orders: { label: "سفارش‌های در جریان", unit: "سفارش", icon: "orders" },
  commission: { label: "پورسانت این ماه", unit: "تومان", icon: "commission" },
  visits: { label: "بازدید این ماه", unit: "بازدید", icon: "visits" },
};

/* ---------- کمک‌کننده‌های فرمت اعداد فارسی ---------- */
function formatNumber(n) {
  return Number(n ?? 0).toLocaleString("fa-IR");
}
function formatDelta(delta) {
  const d = Number(delta ?? 0);
  const sign = d >= 0 ? "+" : "-";
  return `${sign}${Math.abs(d).toLocaleString("fa-IR")}٪`;
}
function trendOf(delta) {
  return Number(delta ?? 0) >= 0 ? "up" : "down";
}

/* ---------- سلام و احوال‌پرسی بر اساس ساعت روز ---------- */
const getGreetingWord = () => {
  const h = new Date().getHours();
  if (h >= 5 && h < 12) return "صبح بخیر";
  if (h >= 12 && h < 17) return "ظهر بخیر";
  if (h >= 17 && h < 20) return "عصر بخیر";
  return "شب بخیر";
};

const SUB_MESSAGES = [
  "امروز هم یه روز خوب برای فروش بیشتره ✨",
  "بریم یه نگاهی به وضعیت فروشگاه‌هات بندازیم 🚀",
  "خلاصه‌ی وضعیت فروشگاه‌های شما همین‌جاست 📊",
  "امیدواریم امروز روز پرباری داشته باشی 🌱",
  "یه نگاه سریع به اعداد امروزت بنداز 👇",
  "فروشگاهت داره خوب پیش می‌ره، ادامه بده! 💪",
  "چیزی که امروز نیاز داری، همین زیره 🔍",
];

const QUICK_ACTIONS = [
  {
    id: "new-ticket",
    label: "ثبت تیکت جدید",
    icon: "ticket",
    route: "new-ticket",
  },
  {
    id: "register-site",
    label: "ثبت سایت جدید",
    icon: "site",
    route: "sites-requests",
  },
  {
    id: "manage-site",
    label: "مدیریت سایت",
    icon: "settings",
    route: "my-sites",
  },
  {
    id: "manage-orders",
    label: "مدیریت سفارش‌ها",
    icon: "orders",
    route: "manage-orders",
  },
];

/* ---------- بنر معرفی قابلیت پرو مجازی ---------- */
const TryOnBanner = () => (
  <section className="tob" aria-labelledby="tob-title">
    <span className="tob-icon">
      <Icon>{ICONS.sparkle}</Icon>
    </span>
    <div className="tob-content">
      <h2 id="tob-title" className="tob-title">
        اتاق پرو مجازی به دکانا اضافه شد
      </h2>
      <p className="tob-text">
        فروشگاه‌هایی که محصولشون نیاز به پرو و تست داره، مثل پوشاک، عینک و
        اکسسوری، می‌تونن اتاق پرو مجازی رو برای محصولاتشون فعال کنن تا مشتری قبل
        از خرید تجربه‌ی دقیق‌تری داشته باشه و مرجوعی کمتر بشه.
      </p>
    </div>
    <a
      className="tob-cta"
      href={VIRTUAL_TRYON_TUTORIAL_URL}
      target="_blank"
      rel="noopener noreferrer"
    >
      <Icon>{ICONS.video}</Icon>
      ویدیو آموزشی
    </a>

    {/* ---------- تصویر اسکن اتاق پرو ---------- */}
    <div className="tob-visual" aria-hidden="true">
      <svg viewBox="0 0 260 300" className="tob-svg">
        <defs>
          <linearGradient id="tobScanGlow" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#c9a8ff" stopOpacity="0" />
            <stop offset="100%" stopColor="#c9a8ff" stopOpacity="0.55" />
          </linearGradient>
          <clipPath id="tobFrameClip">
            <rect x="8" y="8" width="244" height="284" rx="14" />
          </clipPath>
        </defs>

        <g clipPath="url(#tobFrameClip)">
          {/* بدن + لباس */}
          <path
            d="M44 300 C44 246 84 224 130 224 C176 224 216 246 216 300 Z"
            fill="rgba(255,255,255,0.10)"
          />
          <path
            d="M66 300 V252 Q66 234 90 229 L112 223 Q130 240 148 223 L170 229 Q194 234 194 252 V300 Z"
            fill="rgba(255,92,138,0.28)"
            stroke="#ff5c8a"
            strokeWidth="1.6"
            strokeDasharray="5 4"
          />
          {/* گردن و سر */}
          <rect
            x="118"
            y="198"
            width="24"
            height="30"
            rx="8"
            fill="rgba(255,255,255,0.14)"
          />
          <ellipse
            cx="130"
            cy="148"
            rx="46"
            ry="56"
            fill="rgba(255,255,255,0.18)"
          />
          {/* عینک */}
          <g
            fill="rgba(255,92,138,0.22)"
            stroke="#ff5c8a"
            strokeWidth="3"
            strokeLinecap="round"
          >
            <rect x="82" y="132" width="40" height="28" rx="11" />
            <rect x="138" y="132" width="40" height="28" rx="11" />
            <path d="M122 143 Q130 137 138 143" fill="none" />
            <path d="M82 141 L66 137M178 141 L194 137" fill="none" />
          </g>
          {/* کانتور و نقاط چهره */}
          <ellipse
            cx="130"
            cy="148"
            rx="56"
            ry="68"
            fill="none"
            stroke="rgba(255,255,255,0.4)"
            strokeWidth="1.2"
            strokeDasharray="3 5"
          />
          <g fill="#ffffff">
            <circle cx="130" cy="94" r="2.6" />
            <circle cx="130" cy="168" r="2.6" />
            <circle cx="94" cy="190" r="2.6" />
            <circle cx="166" cy="190" r="2.6" />
            <circle cx="130" cy="204" r="2.6" />
          </g>

          {/* خط اسکن */}
          <g className="tob-scan">
            <rect
              x="8"
              y="0"
              width="244"
              height="34"
              fill="url(#tobScanGlow)"
            />
            <rect x="8" y="33" width="244" height="2" fill="#ffffff" />
          </g>
        </g>

        {/* گوشه‌های قاب */}
        <g fill="none" stroke="#e9d9ff" strokeWidth="3" strokeLinecap="round">
          <path d="M8 42 V20 Q8 8 20 8 H42" />
          <path d="M218 8 H240 Q252 8 252 20 V42" />
          <path d="M8 258 V280 Q8 292 20 292 H42" />
          <path d="M218 292 H240 Q252 292 252 280 V258" />
        </g>
      </svg>
    </div>
  </section>
);

export default function Dashboard({ onNavigate }) {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const todayLabel = useMemo(
    () =>
      new Date().toLocaleDateString("fa-IR", {
        weekday: "long",
        day: "numeric",
        month: "long",
      }),
    [],
  );

  const greetingWord = useMemo(() => getGreetingWord(), []);
  const subMessage = useMemo(
    () => SUB_MESSAGES[Math.floor(Math.random() * SUB_MESSAGES.length)],
    [],
  );

  const [chartPeriod, setChartPeriod] = useState("monthly");
  const [visitsPeriod, setVisitsPeriod] = useState("monthly");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [yearlySales, setYearlySales] = useState([]);
  const [monthlySales, setMonthlySales] = useState([]);
  const [yearlyVisits, setYearlyVisits] = useState(FAKE_YEARLY_VISITS);
  const [monthlyVisits, setMonthlyVisits] = useState(FAKE_MONTHLY_VISITS);
  const [orderStatus, setOrderStatus] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [tryOnStores, setTryOnStores] = useState([]);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await getDashboardData();
      setSummary(data.summary);
      setYearlySales(
        (data.sales_by_month ?? []).map((d) => ({
          day: persianMonthName(d.month),
          value: d.sale,
        })),
      );
      setMonthlySales(
        (data.sales_last_30_days ?? []).map((d) => ({
          day: d.day,
          value: d.sale,
        })),
      );
      if ((data.visits_by_month ?? []).length) {
        setYearlyVisits(
          data.visits_by_month.map((d) => ({
            day: persianMonthName(d.month),
            value: d.count,
          })),
        );
      }
      if ((data.visits_last_30_days ?? []).length) {
        setMonthlyVisits(
          data.visits_last_30_days.map((d) => ({
            day: d.day,
            value: d.count,
          })),
        );
      }
      setOrderStatus(
        (data.orders_status_this_month ?? []).map((s) => ({
          id: s.status,
          label: s.label,
          count: s.count,
          percentage: s.percentage,
          color: ORDER_STATUS_COLORS[s.status] ?? "var(--gray-400, #9ca3af)",
        })),
      );
      setRecentOrders(data.latest_orders ?? []);
      setTopProducts(data.top_products_this_month ?? []);
      setTickets(data.latest_tickets ?? []);
      setTryOnStores(
        (data.my_stores ?? []).map((s) => ({
          id: s.store_id,
          name: s.store_name,
          tokens: Number(s.remaining_tokens ?? 0),
        })),
      );
    } catch {
      setError("خطا در دریافت اطلاعات داشبورد. لطفاً دوباره تلاش کنید.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const STATS = useMemo(() => {
    if (!summary) return [];
    return Object.keys(STAT_META).map((id) => {
      const valueKey =
        {
          wallet: "wallet_balance",
          sales: "month_sale",
          orders: "pending_order_count",
          commission: "commission_this_month",
          visits: "visit_this_month",
        }[id] ?? id;
      const deltaKey = `${valueKey}_delta`;
      const delta = summary[deltaKey];
      return {
        id,
        ...STAT_META[id],
        value: formatNumber(summary[valueKey]),
        delta: formatDelta(delta),
        trend: trendOf(delta),
      };
    });
  }, [summary]);

  const chartData = chartPeriod === "yearly" ? yearlySales : monthlySales;
  const visitsChartData =
    visitsPeriod === "yearly" ? yearlyVisits : monthlyVisits;
  const donutTotal = orderStatus.reduce(
    (sum, s) => sum + Number(s.percentage || 0),
    0,
  );

  const tryOnTotal = useMemo(
    () => tryOnStores.reduce((sum, s) => sum + s.tokens, 0),
    [tryOnStores],
  );

  const goTo = (route) => {
    navigate(`/panel/user/${route}`);
  };

  if (loading) {
    return (
      <div className="dash-loading">
        <div className="dash-spinner" />
        <p>در حال بارگذاری داشبورد...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dash-error">
        <span>⚠️</span>
        <p>{error}</p>
        <button type="button" onClick={fetchDashboard}>
          تلاش مجدد
        </button>
      </div>
    );
  }

  return (
    <div className="dash">
      {/* ---------- هدر ---------- */}
      <div className="dash-head">
        <div>
          <h1>
            {greetingWord}
            {user?.first_name ? `، ${user.first_name}` : ""}
          </h1>
          <p className="dash-sub">{subMessage}</p>
        </div>
        <div className="dash-head-right">
          <div className="dash-date">{todayLabel}</div>
          <a
            className="dash-dokana-btn"
            href="/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Icon>{ICONS.externalLink}</Icon>
            ورود به سایت دکانا
          </a>
        </div>
      </div>

      {/* ---------- بنر معرفی قابلیت پرو مجازی ---------- */}
      <TryOnBanner />

      {/* ---------- دسترسی سریع ---------- */}
      <div className="panel panel-actions">
        <div className="panel-head">
          <h2>دسترسی سریع</h2>
        </div>
        <div className="actions-grid">
          {QUICK_ACTIONS.map((a) => (
            <button
              key={a.id}
              type="button"
              className="action-btn"
              onClick={() => goTo(a.route)}
            >
              <span className="action-icon">
                <Icon>{ICONS[a.icon]}</Icon>
              </span>
              {a.label}
            </button>
          ))}
        </div>
      </div>

      {/* ---------- کارت‌های آماری ---------- */}
      <div className="stat-grid">
        {STATS.map((s) => (
          <div className="stat-card" key={s.id}>
            <div className="stat-top">
              <div className="stat-icon">
                <Icon>{ICONS[s.icon]}</Icon>
              </div>
              <div className={`stat-delta ${s.trend}`}>
                <Icon>{ICONS[s.trend === "up" ? "arrowUp" : "arrowDown"]}</Icon>
                {s.delta}
              </div>
            </div>
            <div className="stat-body">
              <div className="stat-label">{s.label}</div>
              <div className="stat-value">
                {s.value}
                <span className="stat-unit">{s.unit}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ---------- توکن پرو مجازی هر فروشگاه ---------- */}
      <div className="panel panel-tryon">
        <div className="panel-head">
          <h2>توکن پرو مجازی فروشگاه‌ها</h2>
          <span className="panel-tag">
            مجموع: {formatNumber(tryOnTotal)} توکن
          </span>
        </div>
        {tryOnStores.length > 0 ? (
          <ul className="tryon-grid">
            {tryOnStores.map((s) => (
              <li
                key={s.id}
                className={`tryon-card${s.tokens <= 0 ? " is-empty" : ""}`}
              >
                <span className="product-icon">
                  <Icon>{ICONS.token}</Icon>
                </span>
                <div className="tryon-body">
                  <span className="tryon-store">{s.name}</span>
                  <span className="tryon-hint">
                    {s.tokens <= 0 ? "توکن تمام شده" : "توکن باقی‌مانده"}
                  </span>
                </div>
                <div className="tryon-amount">
                  {formatNumber(s.tokens)}
                  <span className="tryon-unit">توکن</span>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="dash-empty">فروشگاهی با پرو مجازی فعال یافت نشد.</p>
        )}
      </div>

      {/* ---------- نمودار فروش + بازدید ---------- */}
      <div className="dash-row dash-row-sales-visits">
        <div className="panel panel-chart">
          <div className="panel-head">
            <h2>روند فروش</h2>
            <div className="chart-toggle">
              <button
                type="button"
                className={chartPeriod === "monthly" ? "active" : ""}
                onClick={() => setChartPeriod("monthly")}
              >
                روزانه
              </button>
              <button
                type="button"
                className={chartPeriod === "yearly" ? "active" : ""}
                onClick={() => setChartPeriod("yearly")}
              >
                ماهانه
              </button>
            </div>
          </div>
          {chartPeriod === "yearly" ? (
            <SalesBarChart data={chartData} reverse={false} />
          ) : (
            <SalesAreaChart data={chartData} />
          )}
        </div>

        <div className="panel panel-visits">
          <div className="panel-head">
            <h2>بازدید {visitsPeriod === "yearly" ? "سال" : "ماه"}</h2>
            <div className="chart-toggle">
              <button
                type="button"
                className={visitsPeriod === "monthly" ? "active" : ""}
                onClick={() => setVisitsPeriod("monthly")}
              >
                روزانه
              </button>
              <button
                type="button"
                className={visitsPeriod === "yearly" ? "active" : ""}
                onClick={() => setVisitsPeriod("yearly")}
              >
                ماهانه
              </button>
            </div>
          </div>
          <VisitsRadarChart data={visitsChartData} unit="بازدید" />
        </div>
      </div>

      {/* ---------- سفارش‌های اخیر + وضعیت سفارش‌ها ---------- */}
      <div className="dash-row">
        <div className="panel panel-orders">
          <div className="panel-head">
            <h2>سفارش‌های اخیر</h2>
            <button
              type="button"
              className="panel-link"
              onClick={() => goTo("manage-orders")}
            >
              مشاهده همه
            </button>
          </div>
          <div className="dashboard-orders-table-scroll">
            <div className="dashboard-orders-table">
              <div className="orders-row orders-row-head">
                <span>مشتری</span>
                <span>فروشگاه</span>
                <span>مبلغ</span>
                <span>وضعیت</span>
                <span>تاریخ</span>
              </div>
              {recentOrders.map((o) => (
                <div className="orders-row" key={o.id}>
                  <span data-label="مشتری">{o.customer_name}</span>
                  <span className="muted" data-label="فروشگاه">
                    {o.store_name}
                  </span>
                  <span className="mono" data-label="مبلغ">
                    {formatNumber(o.total_price)} تومان
                  </span>
                  <span data-label="وضعیت">
                    <span className={`badge badge-${o.state}`}>{o.status}</span>
                  </span>
                  <span className="muted" data-label="تاریخ">
                    {convertToPersianDigits(
                      moment(o.created_at).format("jYYYY/jMM/jDD"),
                    )}
                  </span>
                </div>
              ))}
              {recentOrders.length === 0 && (
                <p className="dash-empty">سفارشی یافت نشد.</p>
              )}
            </div>
          </div>
        </div>

        <div className="panel panel-status">
          <div className="panel-head">
            <h2>وضعیت سفارش‌ها</h2>
            <span className="panel-tag">این ماه</span>
          </div>
          <div className="donut-wrap">
            <StatusDonutChart data={orderStatus} />
            <div className="donut-center">
              <span className="donut-total">
                {Math.round(donutTotal).toLocaleString("fa-IR")}٪
              </span>
              <span className="donut-total-label">مجموع</span>
            </div>
          </div>
          <ul className="status-legend">
            {orderStatus.map((s) => (
              <li key={s.id}>
                <span className="dot" style={{ background: s.color }} />
                <span className="legend-label">{s.label}</span>
                <span className="legend-value">
                  {Math.round(Number(s.percentage) || 0).toLocaleString(
                    "fa-IR",
                  )}
                  ٪
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* ---------- پرفروش‌ترین محصولات + تیکت‌های پشتیبانی ---------- */}
      <div className="dash-row dash-row-secondary">
        <div className="panel panel-products">
          <div className="panel-head">
            <h2>پرفروش‌ترین محصولات</h2>
            <span className="panel-tag">این ماه</span>
          </div>
          <ul className="product-list">
            {topProducts.map((p, i) => (
              <li key={p.id} className="product-row">
                <span className="product-rank">{i + 1}</span>
                <span className="product-icon">
                  <Icon>{ICONS.product}</Icon>
                </span>
                <div className="product-body">
                  <span className="product-name">{p.product_name}</span>
                  <span className="product-sales">{p.total_quantity} فروش</span>
                </div>
                <div className={`product-amount ${p.trend}`}>
                  {formatNumber(p.total_sales)} تومان
                </div>
              </li>
            ))}
            {topProducts.length === 0 && (
              <p className="dash-empty">محصولی یافت نشد.</p>
            )}
          </ul>
        </div>

        <div className="panel panel-tickets">
          <div className="panel-head">
            <h2>تیکت‌های پشتیبانی</h2>
            <button
              type="button"
              className="panel-link"
              onClick={() => goTo("ticket-archive")}
            >
              مشاهده همه
            </button>
          </div>
          <ul className="ticket-list">
            {tickets.map((t) => (
              <li key={t.id} className="dashboard-ticket-row">
                <span className="ticket-icon">
                  <Icon>{ICONS.ticket}</Icon>
                </span>
                <div className="ticket-body">
                  <span className="ticket-subject">{t.subject}</span>
                  <span className="ticket-meta">
                    #{t.id} ·{" "}
                    {convertToPersianDigits(
                      moment(t.created_at).format("HH:MM - jYYYY/jMM/jDD"),
                    )}
                  </span>
                </div>
                <span className={`badge badge-ticket-${t.state}`}>
                  {t.status}
                </span>
              </li>
            ))}
            {tickets.length === 0 && (
              <p className="dash-empty">تیکتی یافت نشد.</p>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
}
